import { AfterViewInit, Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { IDriftEntryDto } from 'src/app/models/features/schema-drift/IDriftEntryDto';
import { HeaderService } from '../../../../services/_app/header/header.service';
import { SchemaDriftService } from '../../../../services/_app/schema-drift/schema-drift.service';
import { AuditProfileService } from '../../../../services/audit-profile/audit-profile.service';
import { BaseComponent } from '../../../base/base.component';
import { NgxChartsModule, Color, ScaleType } from '@swimlane/ngx-charts';

@Component({
  selector: 'app-drift-calendar-heatmap',
  templateUrl: './drift-calendar-heatmap.component.html',
  styleUrls: ['./drift-calendar-heatmap.component.css']
})
export class DriftCalendarHeatmapComponent extends BaseComponent implements OnInit, AfterViewInit {
  // *********************
  // variables declaration
  // *********************

  // private
  // ********

  // public
  // ******
  @Input() driftData: any;
  @Input() isDark = false;

  public chartData: any[] = [];
  public view: [number, number] = [950, 400];
  public showLegend = true;
  public xAxisLabel = 'Date';
  public yAxisLabel = 'Schema Changes';
  public legendTitle = 'Change Type';
  public colorScheme: Color = {
    name: 'schemaDrift',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#16a34a', '#facc15', '#dc2626']
  };
  public showDriftDetails = false;
  public selectedCell: any = null;
  public selectedDrifts: IDriftEntryDto[] = [];

  constructor(private auditProfileService: AuditProfileService,
    private headerService: HeaderService,
    private schemaDriftService: SchemaDriftService,
    private router: Router) {
    super();
  }

  // ***************
  // lifecycle hooks
  // ***************

  override async ngOnInit() {
    await this.housekeeping();
  }

  ngAfterViewInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('DriftCalendarHeatmapComponent - ngOnChanges', changes);
    if (changes['driftData']) {
      this.tryBuild();
    }
  }

  // *******
  // methods
  // *******

  // public
  // ******

  public getTotal(seriesGroup: any): number {
    return seriesGroup?.series?.reduce((acc: number, cur: any) => acc + cur.value, 0) || 0;
  }

  /**
   * Fired when user clicks a cell in the heatmap.
   */
  public onHeatmapSelect(event: any): void {
    console.log('Heatmap cell selected:', event);
    this.selectedCell = event;

    // Match entries from driftData within the same day/week as the clicked cell
    const clickedDate = new Date(event.name);
    const entries: IDriftEntryDto[] = this.driftData?.ParsedDrift ?? [];

    // Compare week numbers or exact date
    const weekNum = this.getWeekNumber(clickedDate);
    this.selectedDrifts = entries.filter(e => this.getWeekNumber(new Date(e.date)) === weekNum);

    this.showDriftDetails = true;
  }

  // private
  // *******

  private async housekeeping() {
    this.tryBuild();
  }

  private tryBuild(): void {
    const entries = this.driftData?.ParsedDrift;

    if (!entries || entries.length === 0) {
      this.chartData = [];

      return;
    }

    // Option A: Monthly
    // this.chartData = this.aggregateByDate(entries);

    // Option B: Monthly
    // this.chartData = this.aggregateByMonthNormalized(entries);

    // Option C: Weekly
    this.chartData = this.aggregateByWeek(entries);

    //let a = JSON.stringify(this.chartData);
    //console.log(a);
    //console.log(this.chartData);
  }

  private aggregateByDateOld(entries: IDriftEntryDto[]): any[] {
    const dateMap = new Map<string, number>();
    for (const e of entries) {
      dateMap.set(e.date, (dateMap.get(e.date) || 0) + 1);
    }
    return Array.from(dateMap.entries()).map(([date, count]) => ({
      name: date,
      value: count
    }));
  }

  private aggregateByMonthNormalized(entries: IDriftEntryDto[]): any[] {
    const monthMap = new Map<string, Map<number, number>>();

    for (const e of entries) {
      const date = new Date(e.date);
      const monthLabel = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      const weekNum = this.getWeekNumber(date);

      if (!monthMap.has(monthLabel)) monthMap.set(monthLabel, new Map());
      const weekMap = monthMap.get(monthLabel)!;
      weekMap.set(weekNum, (weekMap.get(weekNum) || 0) + 1);
    }

    return Array.from(monthMap.entries()).map(([month, weeks]) => ({
      name: month,
      series: Array.from(weeks.entries()).map(([week, count]) => ({
        name: `W${week}`,
        value: count
      }))
    }));
  }

  private aggregateByDate(entries: IDriftEntryDto[]): any[] {
    const monthMap = new Map<string, Map<string, number>>();

    for (const e of entries) {
      const date = new Date(e.date);
      const monthLabel = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      const dayLabel = date.toISOString().split('T')[0];

      if (!monthMap.has(monthLabel)) monthMap.set(monthLabel, new Map());
      const inner = monthMap.get(monthLabel)!;
      inner.set(dayLabel, (inner.get(dayLabel) || 0) + 1);
    }

    // Convert to heatmap data structure
    return Array.from(monthMap.entries()).map(([month, dateMap]) => ({
      name: month,
      series: Array.from(dateMap.entries()).map(([day, count]) => ({
        name: day,
        value: count
      }))
    }));
  }

  private aggregateByWeek(entries: IDriftEntryDto[]): any[] {
    const weekMap = new Map<string, Map<string, number>>();

    for (const e of entries) {
      const date = new Date(e.date);

      // Compute the ISO week number
      const weekNum = this.getWeekNumber(date);
      const monthYear = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      const weekLabel = `${monthYear} - W${weekNum}`;

      // Format the X-axis label (short date)
      const dayLabel = date.toISOString().split('T')[0];

      if (!weekMap.has(weekLabel)) weekMap.set(weekLabel, new Map());
      const inner = weekMap.get(weekLabel)!;
      inner.set(dayLabel, (inner.get(dayLabel) || 0) + 1);
    }

    // Convert to the ngx-charts heatmap format
    return Array.from(weekMap.entries()).map(([week, dateMap]) => ({
      name: week,
      series: Array.from(dateMap.entries()).map(([day, count]) => ({
        name: day,
        value: count
      }))
    }));
  }

  /**
   * Utility: Get ISO week number (1–53)
   */
  private getWeekNumber(date: Date): number {
    const temp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = temp.getUTCDay() || 7;
    temp.setUTCDate(temp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(temp.getUTCFullYear(), 0, 1));
    return Math.ceil((((temp.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }
}
